import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { StripeMetadataInput } from '../../VaultForm';
import { BeneficialOwnerData } from '../../BeneficialOwners';
import { requestBodyFormatter } from './requestBodyFormatter';
import { deleteEmptyFields } from '../../../services/vgs/fields';
import { StripeEnv } from '../../../util/enums';
import logger, { logVgsFormSubmitResponse } from '../../../util/logger';
import { createShortLivedToken } from '../../../services/vault/token';
import VaultSubmitError from '../../../util/VaultSubmitError';

export async function submit({
  form,
  org,
  kybBusinessType,
  kybBusinessStructure,
  kybAddressRegion,
  representativePercentOwnership,
  representativeIsOwner,
  representativeTitle,
  representativeAddressRegion,
  currentUserId,
  provider,
  saveToPayableOrg,
  payableOrg,
  financialAccountRoots,
  beneficialOwnersData,
  statementDescriptor,
  metadata,
  stripeEnv,
  userKyc,
  fundraiserId,
  fundraiserSlug,
}: {
  org?: OrgNode;
  kybBusinessStructure: string;
  kybBusinessType: string;
  kybAddressRegion: string;
  representativeIsOwner: boolean;
  representativeAddressRegion: string;
  representativeTitle: string;
  representativePercentOwnership: number;
  beneficialOwnersData: BeneficialOwnerData[];
  currentUserId?: string;
  form: VGSForm<{
    vaultCreateKybKycSubmit: VaultFormMutationResponse;
  }>;
  provider: VaultProvider;
  saveToPayableOrg: boolean;
  payableOrg?: OrgNode;
  financialAccountRoots: FinancialAccountNode[];
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  stripeEnv: StripeEnv;
  userKyc?: Kyc;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
}): Promise<VaultFormSuccessData> {
  deleteEmptyFields(form);
  const token = await createShortLivedToken();

  const validPayableOrg = // user has clicked save to payable checkbox
    saveToPayableOrg &&
    // payable org has id
    payableOrg?.id !== undefined &&
    // org has id
    org?.id !== undefined &&
    // org id is not same as payable org id
    // prevents duplicate accounts
    payableOrg.id !== org.id;
  const payableOrgId = validPayableOrg ? payableOrg?.id : undefined;
  return new Promise((resolve, reject) => {
    form.submit(
      '/graphql',
      {
        data: requestBodyFormatter({
          org,
          financialAccountRoots,
          kybBusinessType,
          kybBusinessStructure,
          kybAddressRegion,
          representativePercentOwnership,
          representativeIsOwner,
          representativeTitle,
          representativeAddressRegion,
          beneficialOwnersData,
          userKyc,
          fundraiserId,
          fundraiserSlug: fundraiserSlug || undefined,
          gateway: provider,
          stripeData:
            provider === 'stripe'
              ? { stripeEnv, metadata, statementDescriptor }
              : undefined,
          representativeUserId: currentUserId,
          payableOrgId,
          payableOrg,
        }),
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
      async (responseStatus, { data, errors }) => {
        logVgsFormSubmitResponse({
          formName: 'CreateKycKyc',
          responseStatus,
          data,
          errors,
        });
        if (!responseStatus || responseStatus < 200 || responseStatus > 299) {
          if (!responseStatus) {
            logger('Response status is not valid', true, {
              responseStatus,
              data,
              errors,
            });
          }
          reject(
            new VaultSubmitError({
              message: `Submit request responded with ${responseStatus} status`,
              retryable: false,
              type: 'SubmitRequestError',
            }),
          );
        }
        if (data === 'Network Error') {
          reject(
            new VaultSubmitError({
              message: 'Submit request returned Network Error.',
              retryable: false,
              type: 'SubmitRequestError',
            }),
          );
        } else if (typeof data === 'string') {
          reject(
            new VaultSubmitError({
              message: data,
              retryable: false,
              type: 'SubmitRequestUnknownError',
            }),
          );
        } else if (
          !data?.vaultCreateKybKycSubmit?.success &&
          data?.vaultCreateKybKycSubmit?.errors !== undefined &&
          data?.vaultCreateKybKycSubmit?.errors.length > 0
        ) {
          reject(new VaultSubmitError(data.vaultCreateKybKycSubmit.errors[0]));
        } else if (!data?.vaultCreateKybKycSubmit?.success) {
          reject(
            new VaultSubmitError({
              message: `Submit request to resolver failed`,
              retryable: false,
              type: 'SubmitResolverRequestError',
              details: data,
            }),
          );
        } else if (
          data?.vaultCreateKybKycSubmit?.success &&
          !data?.vaultCreateKybKycSubmit?.successData
        ) {
          reject(
            new VaultSubmitError({
              message: `Submit request did not return success data`,
              retryable: false,
              type: 'SubmitRequestSuccessDataError',
            }),
          );
        } else if (
          data?.vaultCreateKybKycSubmit?.success &&
          data?.vaultCreateKybKycSubmit?.successData
        ) {
          resolve(data.vaultCreateKybKycSubmit.successData);
        } else {
          logger('Unknown response error', true, {
            responseStatus,
            data,
            errors,
          });
          reject(
            new VaultSubmitError({
              message: 'Unknown response error',
              retryable: false,
              type: 'UnknownResponseError',
            }),
          );
        }
      },
      (errors) => {
        reject(
          new VaultSubmitError({
            message: errors.join(', '),
            retryable: false,
            type: 'UnknownResponseError',
          }),
        );
      },
    );
  });
}
